import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Onze afzetlanden — dezelfde lijst als de vaste BTW-tarieven voor de marge. */
export const COUNTRIES = ["Nederland", "België", "Duitsland", "Frankrijk"];

const TEXT_FIELDS = [
  { name: "straat", autoComplete: "street-address", span: true },
  { name: "postcode", autoComplete: "postal-code" },
  { name: "plaats", autoComplete: "address-level2" },
];

export const emptyAddress = () => ({ straat: "", postcode: "", plaats: "", land: "Nederland" });

/** Adres uit de CRM-respons naar ons formulier-formaat (tolerant voor null). */
export const toAddress = (raw) => ({
  straat: raw?.straat || raw?.adres || "",
  postcode: raw?.postcode || "",
  plaats: raw?.plaats || raw?.stad || "",
  land: raw?.land || "Nederland",
});

export const cleanAddress = (adres) => ({
  straat: (adres.straat || "").trim(),
  postcode: (adres.postcode || "").trim(),
  plaats: (adres.plaats || "").trim(),
  land: adres.land || "Nederland",
});

export const hasAddress = (raw) => Boolean(raw && (raw.straat || raw.adres) && (raw.plaats || raw.stad));

/** Eén regel: "Straat 1, 1561ZE Krommenie, Nederland" */
export const formatAddress = (raw) => {
  const a = toAddress(raw);
  const regels = [a.straat, [a.postcode, a.plaats].filter(Boolean).join(" "), a.land];
  return regels.filter((r) => r && r.trim()).join(", ");
};

/** Verplichte velden controleren; geeft {"soort.veld": melding} terug. */
export const validateAddress = (soort, adres, melding) => {
  const fouten = {};
  ["straat", "postcode", "plaats", "land"].forEach((veld) => {
    if (!String(adres?.[veld] || "").trim()) fouten[`${soort}.${veld}`] = melding;
  });
  return fouten;
};

/** Herbruikbaar adresblok (registratie én adreswijziging in Mijn account). */
export const AddressFields = ({ soort, titel, adres, errors = {}, onChange, idPrefix = "adres" }) => {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-4" data-testid={`${idPrefix}-${soort}-address`}>
      {titel && <legend className="text-sm font-semibold">{titel}</legend>}
      <div className="grid gap-5 sm:grid-cols-2">
        {TEXT_FIELDS.map(({ name, autoComplete, span }) => {
          const fout = errors[`${soort}.${name}`];
          return (
            <div key={name} className={`space-y-2 ${span ? "sm:col-span-2" : ""}`}>
              <Label htmlFor={`${idPrefix}-${soort}-${name}`}>
                {t(`auth.register.address.fields.${name}`)}
              </Label>
              <Input
                id={`${idPrefix}-${soort}-${name}`}
                autoComplete={autoComplete}
                value={adres[name] || ""}
                onChange={(e) => onChange(soort, name, e.target.value)}
                aria-invalid={Boolean(fout)}
                className={fout ? "border-destructive" : ""}
                data-testid={`${idPrefix}-${soort}-${name}-input`}
              />
              {fout && (
                <p className="text-sm text-destructive" data-testid={`${idPrefix}-${soort}-${name}-error`}>
                  {fout}
                </p>
              )}
            </div>
          );
        })}
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-${soort}-land`}>{t("auth.register.address.fields.land")}</Label>
          <Select value={adres.land || "Nederland"} onValueChange={(v) => onChange(soort, "land", v)}>
            <SelectTrigger id={`${idPrefix}-${soort}-land`} data-testid={`${idPrefix}-${soort}-land-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((land) => (
                <SelectItem key={land} value={land} data-testid={`${idPrefix}-${soort}-land-${land}`}>
                  {land}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </fieldset>
  );
};
